// Code generated by balancegen from data/contracts/chronicle.toml and
// data/contracts/s2s.toml. DO NOT EDIT.
// Regenerate with scripts/gen-data.sh.
//
// The season chronicle's fact contract (chronicle.md, R1166): the kind
// table the client writes the prose from, and the wire shape of a fact as
// the meta server stores it. The words are `chronicle` in gen/strings.ts.

/** The most lines one day page shows, the closing line not counted (chronicle.md §3.2). */
export const CHRONICLE_DAY_LINES_MAX = 8;

/** A topic group. `season` is the season page. */
export type ChronicleGroup = 'war' | 'territory' | 'captains' | 'deep' | 'works' | 'world' | 'season';
/** The order of the groups on a day page. */
export const CHRONICLE_TOPIC_ORDER: readonly ChronicleGroup[] = ['war', 'territory', 'captains', 'deep', 'works', 'world'];

/** How the client writes a param value (chronicle.md §5). */
export type ChronicleParamType = 'int' | 'name' | 'noun' | 'faction' | 'resource' | 'attr';
/** The remark pools in the enum order of the captain attributes; the
 * `voice` param indexes it, and the words are `voice_<pool>` lists. */
export const CHRONICLE_VOICE_POOLS: readonly string[] = ['gunnery', 'helmsmanship', 'quiet_hand', 'engineering', 'command', 'prospecting'];

/** Every fact kind id. The words of a kind are the `kind_<id>` list. */
export type ChronicleKind =
  | 'siege_took'
  | 'siege_mauled'
  | 'siege_repulsed'
  | 'siege_held'
  | 'siege_broken'
  | 'siege_fell'
  | 'siege_opened_attack'
  | 'siege_opened_defend'
  | 'first_siege'
  | 'fleet_lost'
  | 'fleet_lost_captain'
  | 'fleet_killed'
  | 'torpedo_hit'
  | 'torpedo_struck'
  | 'cells_taken'
  | 'territory_lost'
  | 'hold_under_fire'
  | 'captain_recruited'
  | 'captain_captured'
  | 'captain_taken'
  | 'captain_ransomed'
  | 'expedition_ok'
  | 'expedition_core'
  | 'expedition_hurt'
  | 'expedition_hurt_core'
  | 'cache_taken'
  | 'building_done'
  | 'research_done'
  | 'quake_stage'
  | 'truce_ended'
  | 'object_fell'
  | 'season_rank'
  | 'season_score'
  | 'season_honour'
  | 'season_title'
  | 'season_captains'
  | 'season_tech'
  | 'season_most_mined'
  | 'season_most_spent'
  | 'season_most_lost'
  | 'milestone_first_ship'
  | 'milestone_first_tech'
  | 'milestone_first_siege'
  | 'milestone_first_kill'
  | 'milestone_first_loss';

export interface ChronicleParamSpec {
  readonly name: string;
  readonly type: ChronicleParamType;
  readonly optional: boolean;
}

export interface ChronicleKindSpec {
  readonly id: ChronicleKind;
  readonly group: ChronicleGroup;
  /** A headline line: marked on the day page, listed on the season page. */
  readonly moment: boolean;
  /** Repeats inside one day fold into one fact with a `times` param. */
  readonly merge: boolean;
  readonly params: readonly ChronicleParamSpec[];
}

/** Every fact kind, in the order of the contract. */
export const CHRONICLE_KINDS: readonly ChronicleKindSpec[] = [
  { id: 'siege_took', group: 'war', moment: true, merge: false, params: [{ name: 'site', type: 'name', optional: false }, { name: 'foe', type: 'faction', optional: false }] },
  { id: 'siege_mauled', group: 'war', moment: true, merge: false, params: [{ name: 'site', type: 'name', optional: false }, { name: 'foe', type: 'faction', optional: false }] },
  { id: 'siege_repulsed', group: 'war', moment: true, merge: false, params: [{ name: 'site', type: 'name', optional: false }, { name: 'foe', type: 'faction', optional: false }] },
  { id: 'siege_held', group: 'war', moment: true, merge: false, params: [{ name: 'site', type: 'name', optional: false }, { name: 'foe', type: 'faction', optional: false }] },
  { id: 'siege_broken', group: 'war', moment: true, merge: false, params: [{ name: 'site', type: 'name', optional: false }, { name: 'foe', type: 'faction', optional: false }] },
  { id: 'siege_fell', group: 'war', moment: true, merge: false, params: [{ name: 'site', type: 'name', optional: false }, { name: 'foe', type: 'faction', optional: false }] },
  { id: 'siege_opened_attack', group: 'war', moment: false, merge: true, params: [{ name: 'site', type: 'name', optional: false }, { name: 'times', type: 'int', optional: true }] },
  { id: 'siege_opened_defend', group: 'war', moment: false, merge: true, params: [{ name: 'site', type: 'name', optional: false }, { name: 'times', type: 'int', optional: true }] },
  { id: 'first_siege', group: 'war', moment: true, merge: false, params: [{ name: 'site', type: 'name', optional: false }] },
  { id: 'fleet_lost', group: 'war', moment: false, merge: false, params: [{ name: 'fleet', type: 'name', optional: false }] },
  { id: 'fleet_lost_captain', group: 'war', moment: true, merge: false, params: [{ name: 'fleet', type: 'name', optional: false }, { name: 'captain', type: 'name', optional: false }, { name: 'voice', type: 'attr', optional: true }, { name: 'cid', type: 'int', optional: true }] },
  { id: 'fleet_killed', group: 'war', moment: false, merge: false, params: [{ name: 'fleet', type: 'name', optional: false }, { name: 'owner', type: 'name', optional: false }] },
  { id: 'torpedo_hit', group: 'war', moment: false, merge: false, params: [{ name: 'torpedo', type: 'name', optional: false }, { name: 'launcher', type: 'name', optional: false }, { name: 'count', type: 'int', optional: false }] },
  { id: 'torpedo_struck', group: 'war', moment: false, merge: false, params: [{ name: 'torpedo', type: 'name', optional: false }, { name: 'owner', type: 'name', optional: false }, { name: 'count', type: 'int', optional: false }] },
  { id: 'cells_taken', group: 'territory', moment: false, merge: false, params: [{ name: 'cells', type: 'int', optional: false }] },
  { id: 'territory_lost', group: 'territory', moment: false, merge: true, params: [{ name: 'foe', type: 'faction', optional: false }, { name: 'times', type: 'int', optional: true }] },
  { id: 'hold_under_fire', group: 'territory', moment: false, merge: true, params: [{ name: 'foe', type: 'faction', optional: false }, { name: 'times', type: 'int', optional: true }] },
  { id: 'captain_recruited', group: 'captains', moment: false, merge: false, params: [{ name: 'captain', type: 'name', optional: false }, { name: 'voice', type: 'attr', optional: true }, { name: 'cid', type: 'int', optional: true }] },
  { id: 'captain_captured', group: 'captains', moment: true, merge: false, params: [{ name: 'captain', type: 'name', optional: false }, { name: 'ransom', type: 'int', optional: false }] },
  { id: 'captain_taken', group: 'captains', moment: true, merge: false, params: [{ name: 'captain', type: 'name', optional: false }, { name: 'owner', type: 'name', optional: false }, { name: 'ransom', type: 'int', optional: false }] },
  { id: 'captain_ransomed', group: 'captains', moment: false, merge: false, params: [{ name: 'captain', type: 'name', optional: false }, { name: 'voice', type: 'attr', optional: true }, { name: 'cid', type: 'int', optional: true }] },
  { id: 'expedition_ok', group: 'deep', moment: false, merge: false, params: [{ name: 'captain', type: 'name', optional: false }, { name: 'isotopes', type: 'int', optional: false }, { name: 'voice', type: 'attr', optional: true }, { name: 'cid', type: 'int', optional: true }] },
  { id: 'expedition_core', group: 'deep', moment: true, merge: false, params: [{ name: 'captain', type: 'name', optional: false }, { name: 'isotopes', type: 'int', optional: false }, { name: 'voice', type: 'attr', optional: true }, { name: 'cid', type: 'int', optional: true }] },
  { id: 'expedition_hurt', group: 'deep', moment: false, merge: false, params: [{ name: 'captain', type: 'name', optional: false }, { name: 'isotopes', type: 'int', optional: false }, { name: 'voice', type: 'attr', optional: true }, { name: 'cid', type: 'int', optional: true }] },
  { id: 'expedition_hurt_core', group: 'deep', moment: true, merge: false, params: [{ name: 'captain', type: 'name', optional: false }, { name: 'isotopes', type: 'int', optional: false }, { name: 'voice', type: 'attr', optional: true }, { name: 'cid', type: 'int', optional: true }] },
  { id: 'cache_taken', group: 'deep', moment: false, merge: false, params: [{ name: 'finder', type: 'name', optional: false }] },
  { id: 'building_done', group: 'works', moment: false, merge: false, params: [{ name: 'building', type: 'noun', optional: false }, { name: 'level', type: 'int', optional: false }] },
  { id: 'research_done', group: 'works', moment: false, merge: false, params: [{ name: 'tech', type: 'noun', optional: false }] },
  { id: 'quake_stage', group: 'world', moment: false, merge: false, params: [{ name: 'stage', type: 'int', optional: false }] },
  { id: 'truce_ended', group: 'world', moment: false, merge: false, params: [] },
  { id: 'object_fell', group: 'world', moment: false, merge: false, params: [{ name: 'site', type: 'name', optional: false }, { name: 'faction', type: 'faction', optional: false }] },
  { id: 'season_rank', group: 'season', moment: false, merge: false, params: [{ name: 'rank', type: 'int', optional: false }, { name: 'of', type: 'int', optional: false }] },
  { id: 'season_score', group: 'season', moment: false, merge: false, params: [{ name: 'score', type: 'int', optional: false }] },
  { id: 'season_honour', group: 'season', moment: false, merge: false, params: [{ name: 'honour', type: 'int', optional: false }] },
  { id: 'season_title', group: 'season', moment: false, merge: false, params: [{ name: 'title', type: 'noun', optional: false }] },
  { id: 'season_captains', group: 'season', moment: false, merge: false, params: [{ name: 'carried', type: 'int', optional: false }, { name: 'lost', type: 'int', optional: false }] },
  { id: 'season_tech', group: 'season', moment: false, merge: false, params: [{ name: 'branch', type: 'noun', optional: false }, { name: 'nodes', type: 'int', optional: false }] },
  { id: 'season_most_mined', group: 'season', moment: false, merge: false, params: [{ name: 'resource', type: 'resource', optional: false }, { name: 'units', type: 'int', optional: false }] },
  { id: 'season_most_spent', group: 'season', moment: false, merge: false, params: [{ name: 'resource', type: 'resource', optional: false }, { name: 'units', type: 'int', optional: false }] },
  { id: 'season_most_lost', group: 'season', moment: false, merge: false, params: [{ name: 'resource', type: 'resource', optional: false }, { name: 'units', type: 'int', optional: false }] },
  { id: 'milestone_first_ship', group: 'season', moment: false, merge: false, params: [] },
  { id: 'milestone_first_tech', group: 'season', moment: false, merge: false, params: [] },
  { id: 'milestone_first_siege', group: 'season', moment: false, merge: false, params: [] },
  { id: 'milestone_first_kill', group: 'season', moment: false, merge: false, params: [] },
  { id: 'milestone_first_loss', group: 'season', moment: false, merge: false, params: [] },
];

/** The S2S message "ChronicleBatch" (data/contracts/s2s.toml). */
export interface ChronicleBatch {
  world_id: number;
  season: number;
  day: number;
  seq: number;
  pages: ChroniclePage[];
  timelapse?: string;
}

/** The S2S message "ChroniclePage" (data/contracts/s2s.toml). */
export interface ChroniclePage {
  meta_account_id: number;
  rank: number;
  score: number;
  rank_delta: number;
  score_delta: number;
  facts?: ChronicleFact[];
}

/** The S2S message "ChronicleFact" (data/contracts/s2s.toml). */
export interface ChronicleFact {
  kind: string;
  at: number;
  cell?: number;
  params?: Record<string, string>;
  replay?: ChronicleReplay;
}

/** The S2S message "ChronicleReplay" (data/contracts/s2s.toml). */
export interface ChronicleReplay {
  kind?: number;
  fleets?: number[];
  t0?: number;
  t1?: number;
  cell?: number;
}
